import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  addToast
} from "@heroui/react";
import { useDispatch, useSelector } from "react-redux";
import { createHeadingAsync } from "../../../redux/heading";
import { getFullTranscriptionByIdAsync } from "../../../redux/transcription";
import AppButton from "../../common/AppButton";


export const headingTypes = [
  {key: "Heading 1", label: "Heading 1"},
  {key: "Heading 2", label: "Heading 2"},
  {key: "Heading 3", label: "Heading 3"},
  {key: "Heading 4", label: "Heading 4"},
];

const getLevelBadgeClasses = (level) => {
  switch (level) {
    case 1:
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case 2:
      return "bg-sky-100 text-sky-800 border-sky-200";
    case 3:
      return "bg-amber-100 text-amber-800 border-amber-200";
    case 4:
      return "bg-rose-100 text-rose-800 border-rose-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

// Format seconds → hh:mm:ss
function formatTime(seconds) {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// Parse hh:mm:ss → seconds
function parseTime(hhmmss) {
  const parts = hhmmss.split(":").map(parseFloat).reverse();
  const [s = 0, m = 0, h = 0] = parts;
  return h * 3600 + m * 60 + s;
}

function secondsToCustomDateTime(baseDateString, offsetSeconds) {
  const baseDate = new Date(baseDateString);
  const newDate = new Date(baseDate.getTime() + offsetSeconds * 1000);

  const pad = (n) => n.toString().padStart(2, "0");

  const day = pad(newDate.getUTCDate());
  const month = pad(newDate.getUTCMonth() + 1);
  const year = newDate.getUTCFullYear();
  const hours = pad(newDate.getUTCHours());
  const minutes = pad(newDate.getUTCMinutes());
  const seconds = pad(newDate.getUTCSeconds());


  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
}

function isValidHMS(startHMS) {
  const regex = /^([0-1]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
  return regex.test(startHMS) && startHMS !== "00:00:00";
}




const AddHeadingModal = ({
  isOpen,
  onClose,
  defaultStartSeconds = null,
  beforeParagraphId = null,
  onHeadingCreated,
}) => {
  const dispatch = useDispatch();
  const fullTranscription = useSelector((state) => state.transcriptions.fullTranscription);


  const [content, setContent] = useState("");
  const [headingType, setHeadingType] = useState(new Set());
  const [startHMS, setStartHMS] = useState(formatTime(0));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (typeof defaultStartSeconds === "number") {
        setStartHMS(formatTime(defaultStartSeconds));
      }
    }
  }, [isOpen, defaultStartSeconds]);

  const handleSave = async () => {
    let newStartSeconds;
    if (typeof defaultStartSeconds === "number") {
      newStartSeconds = defaultStartSeconds;
    } else {
      if (!isValidHMS(startHMS)) {
        addToast({
          description: "Startzeit muss im Format hh:mm:ss sein und darf nicht 00:00:00 sein",
          color: "danger",
        });
        return;
      }
      newStartSeconds = parseTime(startHMS);
    }
    if (!headingType || headingType.size === 0){
      addToast({
        description: "Bitte wählen Sie einen Überschriftentyp aus",
        color: "danger",
      });

      return
    }
    if (!content || content === ""){
      addToast({
        description: "Bitte geben Sie einen Inhalt ein",
        color: "danger",
      });

      return
    }

    const isoStart = secondsToCustomDateTime(fullTranscription.start, newStartSeconds);

    try {
      setIsSaving(true);
      const action = await dispatch(
        createHeadingAsync({
          start: isoStart,
          content,
          transcription_id: fullTranscription.id,
          type: Array.from(headingType)[0],
          ...(beforeParagraphId ? { before_paragraph_id: beforeParagraphId } : {}),
        })
      );
      if (createHeadingAsync.fulfilled.match(action)) {
        const createdHeading = action.payload;
        if (createdHeading && typeof onHeadingCreated === "function") {
          onHeadingCreated({
            heading: createdHeading,
            startSeconds: newStartSeconds,
            beforeParagraphId,
          });
        }
        await dispatch(getFullTranscriptionByIdAsync(fullTranscription.id));
        setContent("");
        setHeadingType(new Set());
        onClose();
      } else {
        throw new Error(action.error?.message || "Failed to create heading");
      }
    } catch (error) {
      console.error("Failed to save heading", error);
      addToast({
        description: "Überschrift konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
        color: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose} size="lg" placement="center">
      <ModalContent>
        {(modalClose) => (
          <>
            <ModalHeader>Sektion hinzufügen</ModalHeader>
            <ModalBody className="space-y-4">
              <Input
                label="Inhalt"
                placeholder="bsp. 1.1 Sektion"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              {typeof defaultStartSeconds !== "number" && (
                <Input
                  label="Start Time (hh:mm:ss)"
                  placeholder="00:04:02"
                  value={startHMS}
                  onChange={(e) => setStartHMS(e.target.value)}
                />
              )}
              <Select
                className="max-w-xs"
                label="Überschrift"
                placeholder="Überschrift auswählen"
                selectedKeys={headingType}
                onSelectionChange={setHeadingType}
                renderValue={(items) => {
                  return items.map((item) => {
                    const level = parseInt(item.key.replace("Heading ", ""), 10);
                    return (
                      <span key={item.key} className="flex items-center gap-2">
                        <span className={`shrink-0 inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClasses(level)}`}>
                          L{level}
                        </span>
                        {item.key}
                      </span>
                    );
                  });
                }}
              >
                {headingTypes.map((heading) => {
                  const level = parseInt(heading.key.replace("Heading ", ""), 10);
                  return (
                    <SelectItem key={heading.key} textValue={heading.label}>
                      <span className="flex items-center gap-2">
                        <span className={`shrink-0 inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClasses(level)}`}>
                          L{level}
                        </span>
                        {heading.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </Select>
            </ModalBody>
            <ModalFooter>
              <Button variant="bordered" color="danger" radius="full" onPress={onClose} isDisabled={isSaving}>
                abbrechen
              </Button>
              
              <AppButton width="w-[150px]" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Speichern..." : "Änderungen speichern"}
              </AppButton>

            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
export default AddHeadingModal;
